import { fetchDuties } from '@/service/shift/duties';
import { fetchWorkplaces } from '@/service/shift/workplaces';
import { fetchShiftCategories, storeShiftCategories, updateShiftCategories } from '@/service/shift/shiftCategories';
import { storeShiftPatterns, updateShiftPatterns } from '@/service/shift/shiftPatterns';
import { updateShiftCategoriesOrder } from '@/service/shift/shiftCategoriesOrder';
import { timeDiff, addMinute } from '@/util';
import dayjs from 'dayjs';
import { v4 as uuid } from 'uuid';
import cloneDeep from 'lodash.clonedeep';
import { compareShiftCategoricalItem, updateShiftPatternOrders, sortShiftPatterns } from '@/service/shift';

function settingCategoryPatternsCtrl($scope, $modal) {
  const tmpDate = '2020-0101T';
  $scope.isShowUnusedMode = false;
  $scope.tmpEditClass = {};

  $scope.errorMessages = [];

  /**
   * シフトカテゴリ重複バリデーション用データ
   */
  $scope.shiftCategoryDuplicateValidationRule = {
    message: 'カテゴリ名が重複しています',
    items: [],
    value: '',
  };

  /**
   * シフトパターン重複バリデーション用データ
   */
  $scope.shiftPatternsDuplicateValidationRule = {
    message: 'シフトパターン名が重複しています',
    items: [],
    value: '',
  };

  // 業務の更新
  const updateDuties = async () => {
    // 業務の取得
    $scope.duties = await fetchDuties();
    $scope.dutyNames = $scope.duties.map((duty) => duty.dutyName);
    // 擬似的に業務に「休暇」を追加する
    $scope.dutyNames.push('休暇');
  };

  // 持ち場の更新
  const updateWorkplaces = async () => {
    // 持ち場の取得
    const fetchedWorkplaces = await fetchWorkplaces();
    $scope.workplaces = flatGroupedWorkplaces(fetchedWorkplaces);
    $scope.workplaceNames = $scope.workplaces.map((workplace) => workplace.workplaceName);
  };

  /**
   * 重複条件をセット
   */
  const setDuplicateValidationRules = (targetShiftPatternName) => {
    $scope.shiftPatternsDuplicateValidationRule.value = targetShiftPatternName;
    $scope.shiftPatternsDuplicateValidationRule.items = allShiftPatterns()
      .map((shiftPattern) => shiftPattern.shiftPatternName)
      .filter((shiftPatternName) => shiftPatternName !== targetShiftPatternName);
  };

  /**
   * ==========================================
   * シフトカテゴリ
   * ==========================================
   */

  /**
   * シフトカテゴリのコピーデータを作成
   */
  const setClonedShiftCategories = () => {
    $scope.clonedShiftWorkingCategories = cloneDeep($scope.shiftWorkingCategories);
    $scope.clonedShiftAbsenceCategories = cloneDeep($scope.shiftAbsenceCategories);
  };

  const allShiftPatterns = () => {
    return $scope.shiftCategories.flatMap((shiftCategory) => shiftCategory.shiftPatterns);
  };

  /**
   * 勤務と休暇で分割する
   */
  const splitWorkAndBreakShiftCategories = () => {
    $scope.shiftWorkingCategories = $scope.shiftCategories.filter((category) => {
      // カテゴリ名「カスタム」は、編集出来ないように非表示にする（カスタムしたシフトパターンが入るカテゴリ）
      return category.isWorkingCategory && category.shiftCategoryName !== 'カスタム';
    });

    $scope.shiftAbsenceCategories = $scope.shiftCategories.filter((category) => {
      return !category.isWorkingCategory;
    });
  };

  const sortShiftCategories = (shiftCategories) => {
    const categoryMapper = (shiftCategory) => ({
      id: shiftCategory.shiftCategoryId,
      ...shiftCategory,
    });
    shiftCategories.sort((a, b) => compareShiftCategoricalItem(categoryMapper(a), categoryMapper(b)));
  };

  /**
   * シフトカテゴリの並びを更新
   */
  const updateShiftCategoryOrders = async () => {
    const shiftCategoryIds = [...$scope.shiftWorkingCategories, ...$scope.shiftAbsenceCategories]
      .filter((shiftCategory) => !shiftCategory.disabled)
      .map((shiftCategory) => shiftCategory.shiftCategoryId);
    await updateShiftCategoriesOrder({ shiftCategoryIds });
    // 編集後のデータで並べ替え
    sortShiftCategories($scope.shiftCategories);
    $scope.$digest();
  };

  /**
   * シフトカテゴリ名一覧を取得
   */
  const getShiftCategoryNames = () => {
    return $scope.shiftCategories.map((shiftCategory) => {
      return shiftCategory.shiftCategoryName;
    });
  };

  /**
   * シフトカテゴリ名の重複チェック用の配列を作成
   */
  const createDuplicateCategoryNames = () => {
    const shiftCategoryNames = getShiftCategoryNames();

    const mapCategory = (shiftCategory) => {
      return {
        ...shiftCategory,
        shiftCategoryNames: shiftCategoryNames.filter((name) => {
          return name !== shiftCategory.shiftCategoryName;
        }),
      };
    };

    $scope.shiftWorkingCategories = $scope.shiftWorkingCategories.map(mapCategory);
    $scope.shiftAbsenceCategories = $scope.shiftAbsenceCategories.map(mapCategory);
  };

  /**
   * シフトパターン名の重複チェック用の配列を作成
   */
  const createDuplicatePatternNames = (shiftPatternName) => {
    return allShiftPatterns()
      .map((shiftPattern) => shiftPattern.shiftPatternName)
      .filter((name) => name !== shiftPatternName);
  };

  /**
   * シフトカテゴリから保育業務を見つける
   */
  const findChildcareDutyByShiftCategory = () => {
    return $scope.duties.find((duty) => duty.dutyName === '保育');
  };
  /**
   * 持ち場「なし」を取得
   */
  const findEmptyWorkplace = () => {
    return $scope.workplaces.find((workplace) => workplace.workplaceName === 'なし');
  };

  /**
   * カテゴライズされた持ち場を1つの配列にする
   * @param workplaces カテゴライズされた持ち場
   */
  const flatGroupedWorkplaces = (workplaces) => {
    const { classGroupWorkplaces, classWorkplaces, groupWorkplaces, metaWorkplaces } = workplaces;

    // セレクトボックスの選択肢として必要なデータのみの配列を作成
    const flattenedClassGroupWorkplaces = classGroupWorkplaces.map((classGroupWorkplace) => {
      return {
        groupType: 'classGroup',
        workplaceId: classGroupWorkplace.workplaceId,
        workplaceName: classGroupWorkplace.classGroupName,
        colorName: classGroupWorkplace.colorName,
      };
    });
    const flattenedClasses = classWorkplaces.map((classWorkplace) => {
      return {
        groupType: 'class',
        workplaceId: classWorkplace.workplaceId,
        workplaceName: classWorkplace.className,
        colorName: classWorkplace.colorName,
      };
    });
    const flattenedGroups = groupWorkplaces.map((group) => {
      return {
        groupType: 'group',
        workplaceId: group.workplaceId,
        workplaceName: group.groupName,
        colorName: group.colorName,
      };
    });
    const filteredMetaGroups = metaWorkplaces.map((metaWorkplace) => {
      return {
        groupType: 'meta',
        workplaceId: metaWorkplace.workplaceId,
        workplaceName: metaWorkplace.workplaceName,
        colorName: metaWorkplace.colorName,
      };
    });

    return [...flattenedClassGroupWorkplaces, ...flattenedGroups, ...flattenedClasses, ...filteredMetaGroups];
  };

  /** セレクトボックスで使う時間の選択項目を生成 */
  const createTimeSlot = () => {
    $scope.shiftTimeSlot = [...Array(24)].flatMap((_, hourIdx) => {
      const formattedHourIdx = dayjs(`2020-01-01T${hourIdx}`).format('HH');
      return [`${formattedHourIdx}:00`, `${formattedHourIdx}:15`, `${formattedHourIdx}:30`, `${formattedHourIdx}:45`];
    });
  };

  const initialShiftPatternAndCategory = async () => {
    createTimeSlot();

    // シフトカテゴリの取得
    $scope.shiftCategories = await fetchShiftCategories(uuid);
    splitWorkAndBreakShiftCategories();
    createDuplicateCategoryNames();

    $scope.$digest();
  };
  initialShiftPatternAndCategory();

  // 複数同時編集できないので編集可能な物のインデックスを保持
  $scope.editMode = {
    shiftCategoryId: '',
    shiftPatternId: '',
  };

  /**
   * シフトカテゴリの並び替え時の要素を操作
   */
  const sortShiftCategoryPatternElements = (element, status) => {
    let categoryElements = element.target.getElementsByClassName('js_pattern_display');
    categoryElements = Array.from(categoryElements).map((categoryElement) => {
      return (categoryElement.style = `display: ${status};`);
    });
  };

  /**
   * カテゴリの並び替えを保存
   */
  const saveSortShiftCategories = async () => {
    const payload = [...$scope.shiftWorkingCategories, ...$scope.shiftAbsenceCategories].map((shiftCategory) => {
      return shiftCategory.shiftCategoryId;
    });
    await updateShiftCategoriesOrder({ shiftCategoryIds: payload });
  };

  /**
   * シフトカテゴリの並び替えの描画処理
   */
  $scope.onDragDropShiftCategories = {
    handle: '[data-js=drag_handle]',
    axis: 'y',
    cursor: 'move',
    helper: function (_, ui) {
      ui.children().each(function () {
        $(this).width($(this).width());
      });
      return ui;
    },
    start: (element) => {
      // シフトカテゴリの並び替え中はシフトパターンを非表示にする
      sortShiftCategoryPatternElements(element, 'none');
    },
    stop: (element) => {
      // シフトカテゴリの並び替え中はシフトパターンを表示する
      sortShiftCategoryPatternElements(element, 'contents');
      saveSortShiftCategories();
    },
    placeholder: 'placeholder',
  };

  /**
   * シフトカテゴリの全体が編集モードか確認
   */
  const turnOnShiftCategoriesEditModeEntirety = () => {
    $scope.isFullEditMode = true;
  };

  const turnOffShiftCategoriesEditModeEntirety = () => {
    $scope.isFullEditMode = false;
    $scope.editMode.shiftCategoryId = '';
    $scope.tmpEditClass = {};
  };

  /**
   * シフトカテゴリ設定のinputに入力されたとき
   */
  $scope.onInputShiftCategoryName = (shiftCategoryName) => {
    $scope.shiftCategoryDuplicateValidationRule.value = shiftCategoryName;
    $scope.$apply();
  };

  /**
   * シフトパターン設定のinputに入力されたとき
   */
  $scope.onInputShiftPatternName = (shiftPatternName) => {
    $scope.shiftPatternsDuplicateValidationRule.value = shiftPatternName;
    $scope.$apply();
  };

  const setShiftCategoryDuplicateValidationRule = (shiftCategory) => {
    // バリデーション条件をセット
    $scope.shiftCategoryDuplicateValidationRule.value = shiftCategory.shiftCategoryName;
    $scope.shiftCategoryDuplicateValidationRule.items = $scope.shiftCategories
      .flatMap((filteredShiftCategory) => filteredShiftCategory.shiftCategoryName)
      .filter((filteredShiftCategoryName) => filteredShiftCategoryName !== shiftCategory.shiftCategoryName);
  };

  /**
   * シフトカテゴリの編集
   */
  $scope.editSettingCategories = (shiftCategory) => {
    setClonedShiftCategories();
    $scope.editMode.shiftCategoryId = shiftCategory.shiftCategoryId;
    setShiftCategoryDuplicateValidationRule(shiftCategory);

    turnOnShiftCategoriesEditModeEntirety();
    $scope.tmpEditClass = cloneDeep(shiftCategory);
  };

  /**
   * 勤務シフトカテゴリの作成
   */
  const createShiftWorkingCategory = () => {
    // 重複バリデーション用の配列を作成
    const shiftCategoryNames = getShiftCategoryNames();
    const newShiftCategory = {
      disabled: false,
      isWorkingCategory: true,
      order: 0,
      shiftCategoryId: '',
      shiftCategoryName: '',
      shiftCategoryNames,
    };

    $scope.shiftWorkingCategories.unshift(newShiftCategory);
    $scope.tmpEditClass = cloneDeep(newShiftCategory);
  };

  /**
   * 休暇シフトカテゴリの作成
   */
  const createShiftAbsenceCategory = () => {
    // 重複バリデーション用の配列を作成
    const shiftCategoryNames = getShiftCategoryNames();
    const newShiftCategory = {
      disabled: false,
      isWorkingCategory: false,
      order: 0,
      shiftCategoryId: '',
      shiftCategoryName: '',
      shiftCategoryNames,
    };

    $scope.shiftAbsenceCategories.unshift(newShiftCategory);
    $scope.tmpEditClass = cloneDeep(newShiftCategory);
  };

  /** シフトカテゴリを追加をクリックで発火 */
  $scope.onClickCreateShiftCategory = (type) => {
    $scope.editMode.shiftCategoryId = '';
    turnOnShiftCategoriesEditModeEntirety();
    setClonedShiftCategories();
    setShiftCategoryDuplicateValidationRule({ shiftCategoryName: '' });

    if (type === 'work') {
      createShiftWorkingCategory();
    } else if (type === 'absence') {
      createShiftAbsenceCategory();
    } else {
      throw new Error('カテゴリのtypeが見つかりません');
    }
  };

  /**
   * 新規カテゴリの作成
   */
  const saveCreateShiftCategory = async (type, targetShiftCategory) => {
    // API通信
    const payload = {
      isWorkingCategory: targetShiftCategory.isWorkingCategory,
      shiftCategoryName: targetShiftCategory.shiftCategoryName,
    };
    const createdCategory = await storeShiftCategories(payload);

    // カテゴリの中にシフトパターンをセット出来るよう配列を用意
    createdCategory.shiftPatterns = [];

    if (type === 'work') {
      $scope.shiftWorkingCategories = $scope.shiftWorkingCategories.filter(
        (shiftWorkingCategory) => shiftWorkingCategory.shiftCategoryId
      );
      $scope.shiftWorkingCategories.unshift(createdCategory);
    } else {
      $scope.shiftAbsenceCategories = $scope.shiftAbsenceCategories.filter(
        (shiftAbsenceCategory) => shiftAbsenceCategory.shiftCategoryId
      );
      $scope.shiftAbsenceCategories.unshift(createdCategory);
    }

    $scope.$digest();
  };

  /**
   * 既存データの編集
   */
  const editShiftCategory = async (type, targetShiftCategory, shiftPatterns) => {
    const {
      shiftCategoryId,
      disabled,
      isWorkingCategory,
      shiftCategoryName,
      version,
      shiftCategoryNames,
      isCategoryOpen,
      order,
    } = targetShiftCategory;

    // API通信
    const payload = {
      disabled,
      isWorkingCategory,
      shiftCategoryName,
      version,
    };
    const createdShiftCategory = await updateShiftCategories(shiftCategoryId, payload);

    // 描画で使うオブジェクトをセット
    const shiftCategoryViewObject = {
      ...createdShiftCategory,
      shiftCategoryNames,
      shiftPatterns,
      isCategoryOpen,
      order,
    };

    if (type === 'work') {
      // 勤務カテゴリの更新
      const targetIdx = $scope.shiftWorkingCategories.findIndex(
        (shiftWorkingCategory) => shiftWorkingCategory.shiftCategoryId === shiftCategoryViewObject.shiftCategoryId
      );
      $scope.shiftWorkingCategories[targetIdx] = shiftCategoryViewObject;
    } else {
      // 休暇カテゴリの更新
      const targetIdx = $scope.shiftAbsenceCategories.findIndex(
        (shiftAbsenceCategory) => shiftAbsenceCategory.shiftCategoryId === shiftCategoryViewObject.shiftCategoryId
      );
      $scope.shiftAbsenceCategories[targetIdx] = shiftCategoryViewObject;
    }

    await updateShiftCategoryOrders();
  };

  /**
   * シフトカテゴリの保存
   */
  $scope.onClickSaveSettingCategories = async (type, targetShiftCategory) => {
    const foundShiftCategory = $scope.shiftCategories.find((shiftCategory) => {
      return shiftCategory.shiftCategoryId === targetShiftCategory.shiftCategoryId;
    });

    if (foundShiftCategory) {
      const { shiftPatterns } = foundShiftCategory;
      await editShiftCategory(type, targetShiftCategory, shiftPatterns);
    } else {
      await saveCreateShiftCategory(type, targetShiftCategory);
    }

    turnOffShiftCategoriesEditModeEntirety();

    await initialShiftPatternAndCategory();
  };

  /**
   * シフトカテゴリのキャンセル
   */
  $scope.cancelSettingCategories = () => {
    $scope.editMode.shiftCategoryId = '';
    turnOffShiftCategoriesEditModeEntirety();

    $scope.shiftWorkingCategories = $scope.clonedShiftWorkingCategories;
    $scope.shiftAbsenceCategories = $scope.clonedShiftAbsenceCategories;
  };

  /**
   * シフトカテゴリの開閉
   */
  $scope.onClickToggleShiftCategory = (shiftCategory) => {
    shiftCategory.isCategoryOpen = !shiftCategory.isCategoryOpen;
  };

  /**
   * ==========================================
   * シフトパターン
   * ==========================================
   */

  /**
   * シフトの選択可能な休憩時間数の値を取得
   */
  $scope.getBreakLengthInMinutes = () => {
    const breakLength = [];
    for (let breakIdx = 0; breakIdx <= 480; breakIdx = breakIdx + 15) {
      breakLength.push(breakIdx);
    }
    return breakLength;
  };

  /**
   * シフトパターンの並び替え
   */
  $scope.onDragDropShiftPatterns = {
    stop: async (event) => {
      const targetShiftPatterns = JSON.parse(event.target.dataset.sortable);
      if (!Array.isArray(targetShiftPatterns) || targetShiftPatterns.length < 1) {
        return;
      }
      const shiftCategoryId = targetShiftPatterns[0].shiftCategoryId;
      const category = $scope.shiftCategories.find(
        (shiftCategory) => shiftCategory.shiftCategoryId === shiftCategoryId
      );
      if (category) {
        category.shiftPatterns = await updateShiftPatternOrders(targetShiftPatterns);
      }
    },
  };

  /**
   * モーダル用にdutyの休暇を一時的に生成
   */
  const createDutyAbsence = (shiftFrames) => {
    return shiftFrames.map((shiftFrame) => {
      if (!shiftFrame.dutyName) {
        shiftFrame.dutyName = '休暇';
      }
      return shiftFrame;
    });
  };

  /**
   * シフトパターンの各値をセット
   */
  const setShiftPattern = (shiftPattern) => {
    // モーダル展開時、勤務枠と休暇枠を開始時間で並び替え
    const workingAndAbsenceFrames = [...shiftPattern.workingTimeFrames, ...shiftPattern.absenceTimeFrames].sort(
      (a, b) => {
        return a.beginTime < b.beginTime ? -1 : 1;
      }
    );

    return {
      workingTimeFrames: shiftPattern.workingTimeFrames,
      absenceTimeFrames: shiftPattern.absenceTimeFrames,
      workingAndAbsenceFrames: createDutyAbsence(workingAndAbsenceFrames),
      beginTime: shiftPattern.beginTime,
      endTime: shiftPattern.endTime,
      colorName: shiftPattern.colorName,
      breakLengthInMinutes: shiftPattern.breakLengthInMinutes,
      disabled: shiftPattern.disabled,
      shiftPatternId: shiftPattern.shiftPatternId,
      shiftPatternName: shiftPattern.shiftPatternName,
      shiftPatternNames: createDuplicatePatternNames(shiftPattern.shiftPatternName),
    };
  };

  /**
   * 枠の時間の分数を計算
   */
  const calculateFrameMinute = (timeFrame) => {
    const timeLengthInHour = timeDiff(`${tmpDate}${timeFrame.beginTime}`, `${tmpDate}${timeFrame.endTime}`);
    return timeLengthInHour * 60;
  };

  /**
   * 特定の勤務枠と休暇枠の各枠の時間を更新
   */
  const updateWorkingAndAbsenceFrameTime = (selectWorkingAndAbsenceFrame) => {
    let foundShiftFrame = $scope.shiftPatternForModalView.workingAndAbsenceFrames.find((workingAndAbsenceFrame) => {
      return workingAndAbsenceFrame.uuid === selectWorkingAndAbsenceFrame.uuid;
    });
    foundShiftFrame.workingAndAbsenceLengthInMinutes = calculateFrameMinute(foundShiftFrame);
  };

  /**
   * 実働時間
   */
  const actualWorkingMinute = () => {
    const filteredWorkingFrames = filterWorkingFrames($scope.shiftPatternForModalView.workingAndAbsenceFrames);
    const workingTotalTime = calculateShiftFrameWorkingTotalTime(filteredWorkingFrames);
    // 勤務時間から休憩時間を引いて、実働時間を算出する
    return workingTotalTime - $scope.shiftPatternForModalView.breakLengthInMinutes;
  };

  /**
   * 勤務枠の時間を取得
   */
  const getWorkingFrameTime = () => {
    const actualTime = actualWorkingMinute();
    $scope.shiftPatternForModalView.workingLengthInMinutes = actualTime;
    $scope.shiftPatternForModalView.workingLengthInHour = actualTime / 60;
  };

  /**
   * 勤務枠のみにフィルタリングする
   */
  const filterWorkingFrames = (workingAndAbsenceFrames) => {
    return workingAndAbsenceFrames.filter((workingAndAbsenceFrame) => {
      return workingAndAbsenceFrame.dutyName !== '休暇';
    });
  };

  /**
   * 休暇枠のみにフィルタリングする
   */
  const filterAbsenceFrames = (workingAndAbsenceFrames) => {
    return workingAndAbsenceFrames.filter((workingAndAbsenceFrame) => {
      return workingAndAbsenceFrame.dutyName === '休暇';
    });
  };

  /**
   * 勤務枠の合計時間を算出
   */
  const calculateShiftFrameWorkingTotalTime = (workingFrames) => {
    const initialValue = 0;
    return workingFrames.reduce((accumulator, currentValue) => {
      return accumulator + currentValue.workingAndAbsenceLengthInMinutes;
    }, initialValue);
  };

  /**
   * 各時間枠の入力が正しいか確認
   */
  $scope.validateTimeFrames = () => {
    return $scope.shiftPatternForModalView.workingAndAbsenceFrames.every((workingAndAbsenceFrame) => {
      // 枠の時間が0分以上ならTRUE
      return workingAndAbsenceFrame.workingAndAbsenceLengthInMinutes > 0;
    });
  };

  /**
   * シフトパターンの勤務と休暇の時間選択時のイベント処理
   */
  $scope.onChangeShiftPatternTimeSelect = (selectWorkingAndAbsenceFrame) => {
    updateWorkingAndAbsenceFrameTime(selectWorkingAndAbsenceFrame);
    getWorkingFrameTime();
    $scope.validateTimeFrames();
    $scope.$digest();
  };

  /**
   * シフトパターンの業務選択時のイベント処理
   */
  $scope.onChangeShiftPatternDuty = () => {
    // 業務選択時に勤務枠のみに絞り込み、労働時間を算出する
    getWorkingFrameTime();
    $scope.$digest();
  };

  /**
   * 各枠の合計分数の取得
   */
  const getTimeFrameTotalMinute = () => {
    $scope.shiftPatternForModalView.workingAndAbsenceFrames =
      $scope.shiftPatternForModalView.workingAndAbsenceFrames.map((workingAndAbsenceFrame) => {
        return {
          ...workingAndAbsenceFrame,
          workingAndAbsenceLengthInMinutes: calculateFrameMinute(workingAndAbsenceFrame),
        };
      });
  };

  /**
   * シフトパターンの初期化
   */
  const initializeShiftPattern = (type, shiftCategoryOrPattern) => {
    // シフトパターンの初期値
    let initializeShiftPatternData = {
      workingTimeFrames: [],
      absenceTimeFrames: [],
      workingAndAbsenceFrames: [],
      shiftPatternId: '',
      shiftPatternName: '',
      shiftPatternNames: [],
      beginTime: '00:00',
      endTime: '00:00',
      workingLengthInHour: 0,
      workingLengthInMinutes: 0,
      breakLengthInMinutes: 0,
      colorName: 'strongCobaltBlue',
      disabled: false,
    };

    if (type === 'shiftPattern') {
      initializeShiftPatternData = setShiftPattern(shiftCategoryOrPattern);
    }

    // モーダル表示用のシフトパターンを用意
    $scope.shiftPatternForModalView = {
      ...initializeShiftPatternData,
      shiftCategoryId: shiftCategoryOrPattern.shiftCategoryId,
      shiftCategoryName: shiftCategoryOrPattern.shiftCategoryName,
      isWorkingCategory: shiftCategoryOrPattern.isWorkingCategory,
      order: shiftCategoryOrPattern.order,
      version: shiftCategoryOrPattern.version,
    };
  };

  const setShiftPatternModalOptions = () => {
    $scope.errorMessages = [];
    return {
      templateUrl: '/manage/src/view/service/shift_v2/setting_shift_pattern_modal.html',
      windowClass: 'anglrC-modal',
      size: 'sm',
      scope: $scope,
      backdrop: 'static',
    };
  };

  /**
   * シフトパターンの作成
   */
  $scope.onClickCreateShiftPatterns = async (shiftCategory) => {
    await updateDuties();
    await updateWorkplaces();
    setDuplicateValidationRules('');
    $scope.patternModal = $modal.open({
      ...setShiftPatternModalOptions(),
      resolve: {
        editItem: () => {
          setCloneShiftPatterns();
          initializeShiftPattern('shiftCategory', shiftCategory);
        },
      },
    });
  };
  /**
   * シフトパターンの編集
   */
  $scope.onClickEditShiftPattern = async (selectedShiftPattern) => {
    await updateDuties();
    await updateWorkplaces();
    setDuplicateValidationRules(selectedShiftPattern.shiftPatternName);
    $scope.patternModal = $modal.open({
      ...setShiftPatternModalOptions(),
      resolve: {
        editItem: () => {
          setCloneShiftPatterns();
          initializeShiftPattern('shiftPattern', selectedShiftPattern);
          getTimeFrameTotalMinute();
          getWorkingFrameTime();
          if ($scope.shiftPatternForModalView && $scope.shiftPatternForModalView.workingLengthInMinutes < 0) {
            $scope.errorMessages = [
              { message: '休憩時間数が実働時間を上回っています。休憩時間数が実働時間を超えないよう変更してください。' },
            ];
          }
        },
      },
    });
  };

  /**
   * シフトパターンの勤務と休暇の作成
   */
  $scope.onClickCreateTimeFrame = () => {
    const dutyChildcare = findChildcareDutyByShiftCategory();

    // なしの持ち場を設定
    const metaWorkplace = findEmptyWorkplace();

    let beginTime = '07:00';
    let endTime = '12:00';

    // 最後の枠を取得
    const lastTimeFrame =
      $scope.shiftPatternForModalView.workingAndAbsenceFrames[
        $scope.shiftPatternForModalView.workingAndAbsenceFrames.length - 1
      ];
    // 最後の枠のendTimeをbeginTimeとして新しい枠を生成
    if (lastTimeFrame) {
      const lastTimeFrameEndTime = lastTimeFrame.endTime;
      if (lastTimeFrameEndTime !== '23:30' && lastTimeFrameEndTime !== '23:45') {
        beginTime = lastTimeFrameEndTime;
        endTime = addMinute(lastTimeFrameEndTime, 30);
      }
    }

    let timeFrame = {
      uuid: uuid(),
      beginTime,
      endTime,
      workingAndAbsenceLengthInMinutes: 0,
      dutyCategory: dutyChildcare.dutyCategory,
      dutyId: dutyChildcare.dutyId,
      dutyName: dutyChildcare.dutyName,
      workplaceId: metaWorkplace.workplaceId,
      workplaceName: metaWorkplace.workplaceName,
    };

    timeFrame.workingAndAbsenceLengthInMinutes = calculateFrameMinute(timeFrame);

    $scope.shiftPatternForModalView.workingAndAbsenceFrames.push(timeFrame);

    getWorkingFrameTime();
    $scope.$digest();
  };

  /**
   * シフトパターンの勤務と休暇を削除
   */
  $scope.onClickDeleteTimeFrame = (selectedTimeFrame) => {
    $scope.shiftPatternForModalView.workingAndAbsenceFrames =
      $scope.shiftPatternForModalView.workingAndAbsenceFrames.filter(
        (timeFrame) => selectedTimeFrame.uuid !== timeFrame.uuid
      );
    getWorkingFrameTime();
    $scope.$digest();
  };

  /**
   * 休憩時間の選択時の処理
   */
  $scope.onChangeBreakTime = () => {
    getWorkingFrameTime();
    $scope.$digest();
  };

  /**
   * シフトパターン（枠全体）の最初の開始時間と最後の終了時間を取得
   */
  const getShiftPatternFirstBeginAndLastEndTimes = (targetShiftPattern) => {
    const timeRanges = targetShiftPattern.workingAndAbsenceFrames
      .map((timeFrame) => {
        return [timeFrame.beginTime, timeFrame.endTime];
      })
      .flat()
      .sort((a, b) => {
        return a < b ? -1 : 1;
      });

    const firstBeginTime = timeRanges.slice(0, 1);
    const lastEndTime = timeRanges.slice(-1);
    return [...firstBeginTime, ...lastEndTime];
  };

  /**
   * シフトカテゴリ一覧から引数で指定したシフトカテゴリを見つける
   */
  const findShiftCategory = (selectedShiftPattern) => {
    return [...$scope.shiftWorkingCategories, ...$scope.shiftAbsenceCategories].find((category) => {
      return category.shiftCategoryId === selectedShiftPattern.shiftCategoryId;
    });
  };

  /**
   * シフトパターン一覧から引数で指定したシフトパターンを見つける
   */
  const findShiftPattern = (selectedShiftPattern) => {
    return allShiftPatterns().find((shiftPattern) => {
      return shiftPattern.shiftPatternId === selectedShiftPattern.shiftPatternId;
    });
  };

  /**
   * 時間枠の開始時間を昇順に並び替え
   * @param {object} workingTimeFrames 勤務枠
   * @param {object} absenceTimeFrames 休暇枠
   */
  const timeFrameBeginTimeInAscOrder = (workingTimeFrames, absenceTimeFrames) => {
    const sortedTimeFrames = {
      workingTimeFrames: [],
      absenceTimeFrames: [],
    };

    const sortTimeFrame = (a, b) => (a.beginTime < b.beginTime ? -1 : 1);
    sortedTimeFrames.workingTimeFrames = workingTimeFrames.sort(sortTimeFrame);
    sortedTimeFrames.absenceTimeFrames = absenceTimeFrames.sort(sortTimeFrame);
  };

  /**
   * 業務一覧から対象の業務を探す
   * @param {string} dutyName 業務の名前
   * @returns {string[]}
   */
  const findDuty = (dutyName) => {
    return $scope.duties.find((duty) => duty.dutyName === dutyName);
  };

  /**
   * 持ち場一覧から対象の持ち場を探す
   * @param {string} workplaceName 持ち場の名前
   * @returns {string[]}
   */
  const findWorkplace = (workplaceName) => {
    return $scope.workplaces.find((workplace) => workplace.workplaceName === workplaceName);
  };

  /** APIリクエスト用にデータを整形する */
  const formatApiRequestWorkingTimeFrame = (workingTimeFrames) => {
    return workingTimeFrames.map((workingTimeFrame) => {
      if (!workingTimeFrame) return [];
      let dutyId = '';
      let workplaceId = '';

      if (workingTimeFrame.dutyName) {
        const foundDuty = findDuty(workingTimeFrame.dutyName);
        dutyId = foundDuty.dutyId;
      }
      if (workingTimeFrame.workplaceName) {
        const foundWorkplace = findWorkplace(workingTimeFrame.workplaceName);
        workplaceId = foundWorkplace.workplaceId;
      }

      return {
        beginTime: workingTimeFrame.beginTime,
        endTime: workingTimeFrame.endTime,
        dutyId,
        workplaceId,
      };
    });
  };

  /** APIリクエスト用にデータを整形する */
  const formatApiRequestAbsenceTimeFrame = (absenceTimeFrames) => {
    return absenceTimeFrames.map((absenceTimeFrame) => {
      if (!absenceTimeFrame) return [];
      return {
        beginTime: absenceTimeFrame.beginTime,
        endTime: absenceTimeFrame.endTime,
      };
    });
  };

  /**
   * APIリクエスト用にデータを整形する
   * @param {string} type HTTPメソッド
   * @param {object} shiftPattern シフトパターン
   * @returns {object}
   */
  const formatApiRequestShiftPattern = (type, shiftPattern) => {
    let putMethodObject = {};
    const workingTimeFrames = formatApiRequestWorkingTimeFrame(shiftPattern.workingTimeFrames);
    const absenceTimeFrames = formatApiRequestAbsenceTimeFrame(shiftPattern.absenceTimeFrames);

    const formateApiRequestObject = {
      workingTimeFrames,
      absenceTimeFrames,
      breakLengthInMinutes: shiftPattern.breakLengthInMinutes,
      colorName: shiftPattern.colorName,
      shiftCategoryId: shiftPattern.shiftCategoryId,
      shiftPatternName: shiftPattern.shiftPatternName,
    };

    if (type === 'put') {
      // putメソッドの場合のみ、versionとdisabledをオブジェクトに追加する
      putMethodObject = {
        version: shiftPattern.version,
        disabled: shiftPattern.disabled,
      };
    }

    return {
      ...formateApiRequestObject,
      ...putMethodObject,
    };
  };

  /**
   * シフトパターンの作成
   */
  const saveCreateShiftPattern = async (targetShiftPattern, foundShiftCategory) => {
    const payload = formatApiRequestShiftPattern('post', targetShiftPattern);
    const response = await storeShiftPatterns(payload);
    if (response.status === 200) {
      foundShiftCategory.shiftPatterns.unshift(response.data);
      $scope.$digest();

      $scope.patternModal.close();
    }

    if (response.status === 400) {
      $scope.errorMessages = response.data.errors;
      $scope.$digest();
    } else {
      $scope.errorMessages = [{ message: 'エラーが発生しました。画面を更新して下さい' }];
      $scope.$digest();
    }
  };

  /**
   * シフトパターンの更新
   */
  const updateShiftPattern = async (targetShiftPattern, foundShiftCategory) => {
    const payload = formatApiRequestShiftPattern('put', targetShiftPattern);
    const response = await updateShiftPatterns(targetShiftPattern.shiftPatternId, payload);

    if (response.status === 200) {
      const updatedShiftPattern = response.data;

      const shiftCategoryPatternTargetIdx = foundShiftCategory.shiftPatterns.findIndex(
        (shiftPattern) => shiftPattern.shiftPatternId === updatedShiftPattern.shiftPatternId
      );
      foundShiftCategory.shiftPatterns[shiftCategoryPatternTargetIdx] = updatedShiftPattern;

      $scope.$digest();
      timeFrameBeginTimeInAscOrder(updatedShiftPattern.workingTimeFrames, updatedShiftPattern.absenceTimeFrames);
      sortShiftPatterns(foundShiftCategory.shiftPatterns);

      $scope.patternModal.close();
    }

    if (response.status === 400) {
      $scope.errorMessages = response.data.errors;
      $scope.$digest();
    } else {
      $scope.errorMessages = [{ message: 'エラーが発生しました。画面を更新して下さい' }];
      $scope.$digest();
    }
  };
  /**
   * シフトパターンの作成 or 更新
   */
  $scope.onClickSaveShiftPatterns = async (targetShiftPattern) => {
    // エラーメッセージを初期化
    $scope.errorMessages = [];

    // シフトパターン（枠全体）の最初の開始時間と最後の終了時間を更新
    const [firstBeginTime, lastEndTime] = getShiftPatternFirstBeginAndLastEndTimes(targetShiftPattern);
    targetShiftPattern.beginTime = firstBeginTime;
    targetShiftPattern.endTime = lastEndTime;

    /**
     * シフトパターン作成モーダルのセレクトボックスでは、
     * 勤務と休暇の枠がひとつの項目になっているため、DB保存前にfilterして分ける必要がある
     */
    targetShiftPattern.workingTimeFrames = filterWorkingFrames(targetShiftPattern.workingAndAbsenceFrames);
    targetShiftPattern.absenceTimeFrames = filterAbsenceFrames(targetShiftPattern.workingAndAbsenceFrames);

    // 既に作成済みのシフトパターンかどうか
    const foundShiftPattern = findShiftPattern(targetShiftPattern);
    // どのシフトカテゴリに属しているか
    const foundShiftCategory = findShiftCategory(targetShiftPattern);

    // シフトパターンが既に存在する場合は更新、存在しない場合は新規作成
    foundShiftPattern
      ? await updateShiftPattern(targetShiftPattern, foundShiftCategory)
      : await saveCreateShiftPattern(targetShiftPattern, foundShiftCategory);
  };

  /**
   * シフトパターンのコピーデータを作成
   */
  const setCloneShiftPatterns = () => {
    $scope.clonedShiftPatterns = cloneDeep(allShiftPatterns());
  };

  /**
   * キャンセル
   */
  $scope.cancelModal = () => {
    $scope.patternModal.close();
  };

  /**
   * 休暇時間数選択セレクトボックスの非活性状態
   * 休暇のみ && 休暇時間数が0
   * @returns boolean
   */
  $scope.breakSelectBoxDisabled = () => {
    return (
      $scope.shiftPatternForModalView.workingAndAbsenceFrames.filter((v) => v.dutyName !== '休暇').length === 0 &&
      $scope.shiftPatternForModalView.workingLengthInMinutes === 0
    );
  };

  $scope.breakSelectBoxType = () => {
    return actualWorkingMinute() < 0 ? 'alert' : '';
  };
}

angular.module('inspinia').controller('settingCategoryPatternsCtrl', settingCategoryPatternsCtrl);
